// Vendor Libs
import {cloneDeep, get, includes, isEmpty, map, now, omit, pick, set, throttle, toLower} from 'lodash';

// Lib App
import actionsServiceCaller from './actions-service-caller';
import filterUserScopes from '../bootstrap-data/user-scopes';

// Lib Core
import {isEqualJson, parseJson} from '../lib-core/object';

// Configs
import preferencesConfig from '../../config/preferences-config';
import servicesConfig from '../../config/services-config';
import servicesFormConfig from '../../config/services-form-config';

const servicesDataKey = 'users';
const formName = 'editUser';
let allowSave = false;

const preferencesMiddleware = store => next => action => {
    const {dispatch, getState} = store;
    next(action);
    const nextState = getState();
    const actions = {
        PREFERENCES_INIT: () => fetchPreferences(dispatch, nextState),
        SEARCH_DATA_FETCH: () => savePreferencesThrottled(dispatch, nextState),
        SEARCH_DATA_RESET: () => savePreferencesThrottled(dispatch, nextState),
    };

    if (action.updatePreferences) {
        savePreferencesThrottled(dispatch, nextState);
    } else if (actions[action.type]) {
        actions[action.type]();
    }
};

function getStoresResetAuth(jsonStores) {
    const stores = {};

    map(preferencesConfig, (_config, storeKey) => {
        const jsonStore = get(jsonStores, storeKey, {});
        const configOmit = get(preferencesConfig, [storeKey, 'authReset']);

        set(stores, storeKey, configOmit ? omit(jsonStore, configOmit) : jsonStore);
    });

    return stores;
}

function fetchPreferences(dispatch, nextState) {
    const authUser = get(nextState.auth, 'user');
    const user = get(nextState.preferences, 'user');

    if (authUser && (!user || user !== authUser)) {
        const dispatchFetched = jsonResponse => {
            const userData = get(jsonResponse, 'content.0');

            if (userData) {
                const {clientId, preferences} = userData;
                const preferencesJson = parseJson(preferences);

                if (preferencesJson && toLower(clientId) === toLower(authUser)) {
                    const stores = getStoresResetAuth(get(preferencesJson, 'stores', {}));

                    dispatch({
                        type: 'PREFERENCES_LOAD',
                        stores,
                        user: clientId,
                    });
                }
            }

            dispatch({
                type: 'PREFERENCES_FETCH_FULFILLED',
            });

            allowSave = true;
        };
        const dispatchError = error => {
            dispatch({
                type: 'PREFERENCES_FETCH_REJECTED',
                error,
            });
        };
        const actionParams = {
            servicesConfig: servicesConfig[servicesDataKey],
            dispatchFetched,
            dispatchError,
        };
        const queryParams = {
            q: `clientId=="${authUser}"`,
            page: 0,
            size: 1,
        };
        dispatch({
            type: 'PREFERENCES_FETCH',
        });

        return Promise.resolve(actionsServiceCaller(actionParams, queryParams));
    }
    allowSave = true;
}

function savePreferences(dispatch, nextState) {
    const user = get(nextState.auth, 'user');

    if (allowSave && user) {
        const prevStores = get(nextState.preferences, 'stores');
        const stores = getStoreData(nextState);
        const data = {
            stores,
            timestamp: now(),
            user: nextState.auth.user,
        };
        const dispatchFetched = () => {
            dispatch({
                type: 'PREFERENCES_SAVE_FILFILLED',
                stores,
                user,
            });
        };
        const dispatchError = () => {
            dispatch({
                type: 'PREFERENCES_SAVE_REJECTED',
                user,
            });
        };
        const actionParams = {
            servicesConfig: servicesFormConfig[formName],
            dispatchFetched,
            dispatchError,
        };
        const formData = {
            clientId: user,
            preferences: JSON.stringify(data),
            scope: filterUserScopes(get(nextState, 'auth.scope')),
        };

        if (!isEqualJson(stores, prevStores)) {
            dispatch({
                type: 'PREFERENCES_SAVE',
                stores,
                user,
            });

            actionsServiceCaller(actionParams, formData);
        }
    }
}

function getStoreData(state) {
    const stores = cloneDeep(get(state.preferences, 'stores') || {});

    map(preferencesConfig, (config, storeKey) => {
        const stateData = get(state, storeKey);

        if (stateData && config) {
            if (config.paths) {
                const {exclude, paths, whitelist} = config;
                let storeData = get(stores, storeKey) || {};

                map(state[storeKey], (data, dataKey) => {
                    if (!whitelist || includes(whitelist, dataKey)) {
                        const saveData = pick(data, paths);

                        if (!isEmpty(saveData)) {
                            set(storeData, dataKey, saveData);
                        }
                    }
                });

                if (exclude) {
                    storeData = omit(storeData, exclude);
                }

                if (whitelist) {
                    storeData = pick(storeData, whitelist);
                }

                if (!isEmpty(storeData)) {
                    set(stores, storeKey, storeData);
                }
            } else {
                set(stores, storeKey, stateData);
            }
        }
    });

    return stores;
}

const savePreferencesThrottled = throttle(savePreferences, 2000, {leading: true, trailing: true});

export default preferencesMiddleware;
